import Box from '@mui/material/Box';
import {
  CustomBlockingRulesOptions,
  type InventoryAndContentState,
  type InventoryContentOption,
} from '@openx/types/targeting/inventoryAndContent';
import { useMemo } from 'react';
import type { AnyAction } from 'redux';
import type { TargetingItemsProps } from '../../types';
import {
  type UseCustomBlockingRulesOperations,
  useCustomBlockingRulesOperations,
} from '../hooks/useCustomBlockingRulesOperations';
import { CustomBlockingRulesItemsRenderer } from './CustomBlockingRulesItemsRenderer';

export interface CustomBlockingRulesItemsProps
  extends TargetingItemsProps<InventoryAndContentState[typeof InventoryContentOption.PUBLISHER_INVENTORY_RULES]> {
  dispatchInventory: (action: AnyAction) => void;
}

export const CustomBlockingRulesItems = ({
  dispatchInventory,
  readonly = true,
  targetingParams: selectedItems,
  isNotLastItem,
  switchState: chipLineSwitchState,
  disabledReasonMessage,
}: CustomBlockingRulesItemsProps) => {
  const selectedDomainsBlockingRules = useMemo(
    () => selectedItems.filter(item => item.domain?.length || item.subdomain?.length),
    [selectedItems]
  );

  const selectedAppBundleIdBlockingRules = useMemo(
    () => selectedItems.filter(item => item.bundle_id?.length),
    [selectedItems]
  );

  const { onRemoveAll, onRemoveSelectedRule, onRemoveSingleEntry } = useCustomBlockingRulesOperations({
    dispatchInventory: dispatchInventory as UseCustomBlockingRulesOperations['dispatchInventory'],
    selectedItems,
  });

  return (
    <>
      {selectedDomainsBlockingRules.length > 0 && (
        <CustomBlockingRulesItemsRenderer
          dispatchInventory={dispatchInventory}
          isNotLastItem={isNotLastItem}
          onRemoveAll={onRemoveAll}
          onRemoveSelectedRule={onRemoveSelectedRule}
          onRemoveSingleEntry={onRemoveSingleEntry}
          readonly={readonly}
          switchState={chipLineSwitchState}
          targetingParams={selectedDomainsBlockingRules}
          type={CustomBlockingRulesOptions.DOMAIN}
          disabledReasonMessage={disabledReasonMessage}
        />
      )}

      {selectedAppBundleIdBlockingRules.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <CustomBlockingRulesItemsRenderer
            dispatchInventory={dispatchInventory}
            isNotLastItem={isNotLastItem}
            onRemoveAll={onRemoveAll}
            onRemoveSelectedRule={onRemoveSelectedRule}
            onRemoveSingleEntry={onRemoveSingleEntry}
            readonly={readonly}
            switchState={chipLineSwitchState}
            targetingParams={selectedAppBundleIdBlockingRules}
            type={CustomBlockingRulesOptions.BUNDLE_ID}
            disabledReasonMessage={disabledReasonMessage}
          />
        </Box>
      )}
    </>
  );
};
